/* <price-match-form> — the "Our Price Promise" modal.
   Business logic (the 20% band, whether a code is issued instantly, and the
   exact discount amount) lives on the backend, never here: any approval rule
   evaluated client-side could be read from view-source and used to always
   land inside the "instant" band, or to spoof a POST straight at the
   endpoint. This element only does a basic sanity check (seen price can't be
   >= our own publicly-displayed price — that's not sensitive, it's already
   visible on the page), submits, and renders whichever state the backend's
   response implies. It never computes a discount or generates a code itself. */
class PriceMatchForm extends HTMLElement {
  constructor() {
    super();
    this.utils = window.PriceMatchUtils;
    this.strings = this.utils.getStrings(this);

    this.endpoint = this.dataset.endpoint;
    // Fallbacks only — correct at initial page load, but this modal lives in
    // a different Shopify section from the one that live-updates on variant
    // change, so these two go stale the moment someone switches variants
    // without a page reload. resolveCurrentVariant() is the source of truth.
    this.currentPriceCents = Number(this.dataset.currentPrice);
    this.initialVariantId = Number(this.dataset.variantId);
    // The buyer's active display currency. Sent with the claim so the backend
    // can route mismatches (Markets) to manual review, and used to format the
    // matched price back to the buyer instead of assuming USD.
    this.currency = this.dataset.currency || 'USD';

    const variantsScript = this.querySelector('[data-pm-variants]');
    this.variantsById = new Map();
    if (variantsScript) {
      try {
        JSON.parse(variantsScript.textContent).forEach((variant) => {
          this.variantsById.set(variant.id, variant.price);
        });
      } catch (error) {
        // Falls back to the initial variant/price below if this ever fails to parse.
      }
    }

    this.states = this.querySelectorAll('[data-pm-state]');
    this.claimForm = this.querySelector('[data-form="claim"]');
    this.claimStatusEl = this.querySelector('[data-form-status]');
    this.claimSubmitButton = this.querySelector('[data-submit-button]');
    this.seenPriceInput = this.querySelector('[data-field="seen_price"]');
    this.copyButton = this.querySelector('[data-copy-code]');
    this.copiedTip = this.querySelector('[data-copied-tip]');
    this.checkoutLink = this.querySelector('[data-checkout-link]');
    this.matchedStatusEl = this.querySelector('[data-form-status-matched]');
    this.chatLink = this.querySelector('[data-chat-link]');

    // Carries the resolved variant/price/email forward from the claim step so
    // the matched/checkout steps (and the review-done confirmation) stay
    // consistent with whatever this specific claim was evaluated against.
    this.pendingClaim = null;
    // The code from the most recent "approved" response — the checkout
    // link needs it to build the /discount/<code> redirect.
    this.matchedCode = null;

    this.onClaimSubmit = this.onClaimSubmit.bind(this);
    this.onApplyAndCheckout = this.onApplyAndCheckout.bind(this);

    this.claimForm.addEventListener('submit', this.onClaimSubmit);
    this.seenPriceInput.addEventListener('input', () => this.formatPriceInput());
    this.copyButton.addEventListener('click', () => this.copyCode());
    this.checkoutLink.addEventListener('click', this.onApplyAndCheckout);

    this.querySelectorAll('[data-back-to-form]').forEach((button) => {
      button.addEventListener('click', () => this.showState('form'));
    });
    this.querySelectorAll('[data-close-modal]').forEach((button) => {
      button.addEventListener('click', () => this.closest('modal-dialog').hide());
    });

    if (this.chatLink) {
      // Deliberately no preventDefault/stopPropagation here: the theme's
      // global .open-chat handler (assets/custom.js.liquid) is a document-
      // level delegated click listener, so it still fires on this same
      // click as it bubbles up and opens the chat widget — this listener
      // only closes our modal so the two don't end up stacked on screen.
      this.chatLink.addEventListener('click', () => {
        this.closest('modal-dialog').hide();
      });
    }

    // Resets to the form state whenever any modal-dialog on the page closes
    // (the X button included), so reopening never shows a stale matched/
    // review/review-done state from a previous submission.
    document.body.addEventListener('modalClosed', () => this.showState('form'));
  }

  showState(name) {
    this.states.forEach((state) => {
      state.classList.toggle('active', state.dataset.pmState === name);
    });
  }

  // Mirrors the design's live formatter: digits + at most one decimal point,
  // max 2 decimal places, thousands separators, a leading '$'.
  formatPriceInput() {
    const input = this.seenPriceInput;
    let raw = input.value.replace(/[^0-9.]/g, '');

    const firstDot = raw.indexOf('.');
    if (firstDot !== -1) {
      raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
    }

    let [whole, decimals] = raw.split('.');
    whole = (whole || '').replace(/^0+(?=\d)/, '');
    if (decimals !== undefined) decimals = decimals.slice(0, 2);

    const wholeFormatted = whole ? Number(whole).toLocaleString('en-US') : '';

    let out = '';
    if (wholeFormatted || decimals !== undefined) {
      out = `$${wholeFormatted}${decimals !== undefined ? `.${decimals}` : ''}`;
    }
    input.value = out;
  }

  // The theme keeps ?variant=<id> in the URL in sync with the live selection
  // (product-info.js's updateURL(), via history.replaceState) even though it
  // doesn't touch this modal's own section — so it's a reliable place to read
  // what's actually selected right now, independent of this element's own
  // (possibly stale) initial data attributes.
  resolveCurrentVariant() {
    const urlVariantId = Number(new URLSearchParams(window.location.search).get('variant'));
    const variantId = urlVariantId && this.variantsById.has(urlVariantId) ? urlVariantId : this.initialVariantId;
    const priceCents = this.variantsById.has(variantId) ? this.variantsById.get(variantId) : this.currentPriceCents;

    return { variantId, currentPriceCents: priceCents };
  }

  parseSeenPriceCents() {
    const digits = this.seenPriceInput.value.replace(/[^0-9.]/g, '');
    if (digits === '') return null;
    const dollars = Number(digits);
    if (Number.isNaN(dollars)) return null;
    return Math.round(dollars * 100);
  }

  // The backend requires seenLink to be a fully-qualified URL (scheme
  // required), but shoppers naturally paste bare links like "amazon.com/..."
  // or "www.site.com/..." with no "https://". Rather than reject those at
  // the front door — or worse, silently fail the /price-match call with a
  // generic error once it reaches the backend — treat a schemeless value as
  // shorthand and add the scheme ourselves before it's ever validated or sent.
  normalizeSeenLink(value) {
    if (!value || /^https?:\/\//i.test(value)) return value;
    return `https://${value}`;
  }

  validateClaimForm(currentPriceCents) {
    const priceField = this.seenPriceInput.closest('[data-modal-field]');
    const linkField = this.claimForm.querySelector('[data-field="seen_link"]').closest('[data-modal-field]');
    const linkValue = this.normalizeSeenLink(this.claimForm.querySelector('[data-field="seen_link"]').value.trim());
    const emailInput = this.claimForm.querySelector('[data-field="email"]');
    const emailField = emailInput.closest('[data-modal-field]');
    const emailValue = emailInput.value.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    let isValid = true;
    const seenPriceCents = this.parseSeenPriceCents();

    if (seenPriceCents === null || seenPriceCents <= 0) {
      this.utils.setFieldError(priceField, this.strings.invalidPrice);
      isValid = false;
    } else if (seenPriceCents >= currentPriceCents) {
      this.utils.setFieldError(priceField, this.strings.priceTooHigh);
      isValid = false;
    } else {
      this.utils.setFieldError(priceField, '');
    }

    if (linkValue.length < 8) {
      this.utils.setFieldError(linkField, this.strings.invalidLink);
      isValid = false;
    } else {
      this.utils.setFieldError(linkField, '');
    }

    if (emailValue === '') {
      this.utils.setFieldError(emailField, this.strings.required);
      isValid = false;
    } else if (!emailPattern.test(emailValue)) {
      this.utils.setFieldError(emailField, this.strings.invalidEmail);
      isValid = false;
    } else {
      this.utils.setFieldError(emailField, '');
    }

    return { isValid, seenPriceCents, linkValue, emailValue };
  }

  async onClaimSubmit(event) {
    event.preventDefault();
    // Guards against a duplicate submission if the customer presses Enter
    // again (or double-clicks) while the first request is still in flight —
    // the button is disabled via setSubmitLoading, but a native form
    // "submit" event fires from Enter regardless of the button's disabled
    // state, so the button alone isn't enough.
    if (this.claimSubmitting) return;

    // Resolved once, right before validating and submitting, so the
    // threshold check and the payload agree with whatever the customer
    // actually has selected at this exact moment — not whatever variant
    // happened to be selected when the page first loaded.
    const { variantId, currentPriceCents } = this.resolveCurrentVariant();

    const { isValid, seenPriceCents, linkValue, emailValue } = this.validateClaimForm(currentPriceCents);
    if (!isValid) return;

    this.claimSubmitting = true;
    this.utils.setStatus(this.claimStatusEl, null, '');
    this.utils.setSubmitLoading(this.claimSubmitButton, true);

    // Carries the resolved variant/price/email forward through the review step
    // and into the cart-add on checkout, so every later step stays consistent
    // with whatever this specific claim was actually evaluated against —
    // even if the customer switches variants again afterward.
    this.pendingClaim = { seenPriceCents, linkValue, variantId, currentPriceCents, email: emailValue };

    try {
      // Single server-authoritative call. The backend re-fetches the real
      // variant price from Shopify (never trusts current_price from here),
      // decides the outcome, and — on approval — mints a customer-locked,
      // single-use, 30-day code. seenPrice is sent in major units (dollars).
      const result = await this.utils.postJSON(this.endpoint, {
        email: emailValue,
        variantId: String(variantId),
        seenPrice: seenPriceCents / 100,
        currency: this.currency,
        seenLink: linkValue,
        pageUrl: window.location.href,
      });

      this.handleClaimResult(result);
    } catch (error) {
      this.utils.setStatus(this.claimStatusEl, 'error', error.message || this.dataset.i18nGenericError);
    } finally {
      this.claimSubmitting = false;
      this.utils.setSubmitLoading(this.claimSubmitButton, false);
    }
  }

  handleClaimResult(result) {
    if (result.outcome === 'approved') {
      this.populateMatchedState(result);
      this.showState('matched');
    } else if (result.outcome === 'pending') {
      // The claim needs manual review. The email was already captured on the
      // claim form and the backend has already notified the team, so there's
      // no second step — go straight to the "we'll be in touch" confirmation.
      const echoEl = this.querySelector('[data-echo-email]');
      if (echoEl && this.pendingClaim && this.pendingClaim.email) {
        echoEl.textContent = this.pendingClaim.email;
      }
      this.showState('review-done');
    } else {
      // 'rejected' (or anything unexpected) — we couldn't verify a lower price.
      this.utils.setStatus(this.claimStatusEl, 'error', this.dataset.i18nGenericError);
    }
  }

  populateMatchedState(result) {
    const priceEl = this.querySelector('[data-matched-price]');
    const codeEl = this.querySelector('[data-matched-code]');
    const originalPriceEl = this.querySelector('[data-original-price]');

    if (priceEl && typeof result.matchedPrice === 'number') {
      // matchedPrice arrives in major units (dollars) from the backend.
      priceEl.textContent = result.matchedPrice.toLocaleString('en-US', {
        style: 'currency',
        currency: this.currency,
      });
    }
    if (codeEl && result.code) {
      codeEl.textContent = result.code;
    }
    // Liquid renders a static original-price string at page load, which goes
    // stale the same way data-current-price does — replace it with whatever
    // variant this specific claim actually resolved against.
    if (originalPriceEl && this.pendingClaim) {
      originalPriceEl.textContent = (this.pendingClaim.currentPriceCents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: this.currency,
      });
    }

    this.matchedCode = result.code || null;

    if (this.checkoutLink) {
      // href is a plain fallback (no-JS, middle-click, open-in-new-tab); the
      // real flow runs through onApplyAndCheckout below, which adds the
      // variant to the cart first — a bare /discount/<code> redirect on its
      // own only activates the code, it never puts anything in the cart.
      if (this.matchedCode) {
        this.checkoutLink.href = `/discount/${encodeURIComponent(this.matchedCode)}?redirect=/cart`;
      } else {
        this.checkoutLink.removeAttribute('href');
      }
    }
  }

  async onApplyAndCheckout(event) {
    if (!this.matchedCode) return;

    event.preventDefault();
    if (this.checkoutSubmitting) return;
    this.checkoutSubmitting = true;

    this.utils.setStatus(this.matchedStatusEl, null, '');
    this.checkoutLink.classList.add('loading');
    this.checkoutLink.setAttribute('aria-disabled', 'true');

    try {
      // Must be the variant this claim was actually created against — the
      // discount is scoped to that specific variant ID server-side, so
      // adding a different one (e.g. if the customer switched variants again
      // after submitting) would add an item the code doesn't apply to.
      const variantId = this.pendingClaim ? this.pendingClaim.variantId : this.initialVariantId;

      // Same _uniquekey_/_associatevariantid/_Lead Time convention the regular
      // PDP "checkout" button uses (assets/no-color-v2.js.liquid) so this line
      // item behaves like any other in the cart/order pipeline (bundle
      // grouping, lead-time display, etc). _best_price_discount is the one
      // addition — a hidden (underscore-prefixed, so not customer-visible)
      // marker so support/ops can tell an order came through this price-match
      // flow and which code was used, just by looking at the line item.
      const now = new Date();
      const datePrefix = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const uniqueGroupKey = `${datePrefix}-${Math.random().toString(36).slice(2, 10)}`;
      const leadTime = document.querySelector('.variantmetafield-lead-time')?.textContent?.trim();

      const properties = {
        _uniquekey_: uniqueGroupKey,
        _associatevariantid: String(variantId),
        _best_price_discount: this.matchedCode,
      };
      if (leadTime) {
        properties['_Lead Time'] = leadTime;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties }] }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error((data && data.description) || 'Could not add this item to your cart.');
      }

      window.location.href = `/discount/${encodeURIComponent(this.matchedCode)}?redirect=/checkout`;
    } catch (error) {
      this.checkoutSubmitting = false;
      this.checkoutLink.classList.remove('loading');
      this.checkoutLink.removeAttribute('aria-disabled');
      this.utils.setStatus(this.matchedStatusEl, 'error', error.message || this.dataset.i18nGenericError);
    }
  }

  copyCode() {
    const code = this.querySelector('[data-matched-code]').textContent;
    const showTip = () => {
      this.copiedTip.classList.add('show');
      window.setTimeout(() => this.copiedTip.classList.remove('show'), 1600);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(showTip).catch(showTip);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
      } catch (error) {
        // ignore — clipboard just won't be populated on this browser
      }
      document.body.removeChild(textarea);
      showTip();
    }
  }
}

customElements.define('price-match-form', PriceMatchForm);
